// Ledenadministratie: lijst, detail, import en export (stap F).
//
// Nieuwe module, bewust buiten app.js: eigen events via data-actie en
// delegatie, geen nieuwe inline handlers. Alleen de standaardtabel roept via
// App.tabelRijKlik terug, omdat die component dat voor alle tabellen zo doet.
//
// Het slot zit in de database (RLS en de veldbewaking uit stap E). Deze pagina
// verbergt of blokkeert wat daar toch zou falen, en toont waarom.
import { getClient } from '../data/supabase.js';
import { state } from '../state.js';
import { heeft } from '../router.js';
import { t, locale } from '../lib/i18n.js';
import { esc } from '../lib/format.js';
import { toast } from '../ui/toast.js';
import { showConfirm } from '../ui/confirm.js';
import { tabelTeken } from '../ui/tabel.js';
import { DOELEN, GEWEIGERD, stelMappingVoor, verwerk, leesCsv } from '../lib/import-leden.js';
import * as exportMod from './export.js';
import { refLabel } from '../ui/ref.js';
import { vraagReden } from '../ui/reden.js';
import { dispatchFout, vernieuwTeller } from './goedkeuringen.js';
import { claimFout } from './mijn.js';
import * as bond from './bond.js';
import { toonDossier } from '../ui/dossier.js';
import { behandelLm } from '../ui/lidmaatschap.js';
import { koppelClubs } from '../lib/club-match.js';
import { nieuwVerzoekVenster } from '../ui/verzoeken.js';

const sb = () => getClient();
const DOELEN_TOESTEMMING = ['beeldmateriaal', 'mailing_extern', 'publicatie_uitslagen'];

let personen = [];
let lidmaatschappen = {};   // person_id -> relatie
let clubsRegister = [];     // actieve clubs uit het register van deze bond (alleen bij de module bond)
let zonderClub = 0;         // aantal leden op de werklijst
let eigenaarschap = null;   // veld -> owner_kind
let aantalDubbelen = 0;
let bondsstatus = {};      // person_id -> graad, schorsing (module bond)
let stilzwijgenBond = false; // kent de bond stilzwijgend akkoord (uitleg bij opzeggen en overstap)
let bondsleden = [];        // leden van deze club bij de bond (club_bondsleden, alleen bij een club)

// ── Hulpjes ────────────────────────────────────────────────────────────────
function datum(iso) {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? iso + 'T00:00:00Z' : iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}
function leeftijd(iso) {
  if (!iso) return '';
  const g = new Date(iso + 'T00:00:00Z'); const n = new Date();
  let j = n.getUTCFullYear() - g.getUTCFullYear();
  if (n.getUTCMonth() < g.getUTCMonth() || (n.getUTCMonth() === g.getUTCMonth() && n.getUTCDate() < g.getUTCDate())) j--;
  return String(j);
}
export function volledigeNaam(p) {
  return [p.voornaam, p.tussenvoegsel, p.achternaam].filter(Boolean).join(' ');
}
function foutTekst(e) {
  const m = String(e?.message || e || '').match(/lidmaatschap:([a-z_]+)/);
  return m ? t('lidm.fout.' + m[1]) : t('alg.fout', { melding: e?.message || String(e) });
}
function fout(e) { toast(foutTekst(e), 'error'); }
function knop(actie, icoon, tekst, soort = 'btn-default', extra = '') {
  return `<button class="btn ${soort} btn-sm" data-actie="${actie}" ${extra}><i class="ti ti-${icoon}"></i> ${esc(tekst)}</button>`;
}

async function laadEigenaarschap() {
  if (eigenaarschap) return eigenaarschap;
  const { data } = await sb().from('field_ownership').select('field, owner_kind').eq('entity', 'persons');
  eigenaarschap = Object.fromEntries((data || []).map(r => [r.field, r.owner_kind]));
  return eigenaarschap;
}

// ── Clubs en werklijst (alleen bij een bond) ───────────────────────────────
async function laadClubs() {
  clubsRegister = []; zonderClub = 0; stilzwijgenBond = false;
  if (!bond.actief()) return;
  const [c, w, i] = await Promise.all([
    sb().from('club_register').select('id, ref, naam, plaats').eq('organisation_id', state.currentOrgId).eq('status', 'actief').order('naam'),
    sb().from('lidmaatschap_werklijst').select('relation_id', { count: 'exact' }).eq('organisation_id', state.currentOrgId),
    heeft('case.read') ? sb().rpc('bond_instellingen_lezen', { p_bond: state.currentOrgId }) : Promise.resolve({ data: null }),
  ]);
  clubsRegister = c.data || [];
  stilzwijgenBond = !!(Array.isArray(i.data) ? i.data[0] : i.data)?.stilzwijgend_akkoord;
  zonderClub = w.count ?? w.data?.length ?? 0;
}
const clubNaam = id => clubsRegister.find(c => c.id === id)?.naam || '';
const clubOpties = (gekozen, leeg) => `<option value="">${esc(leeg)}</option>`
  + clubsRegister.map(c => `<option value="${c.id}"${c.id === gekozen ? ' selected' : ''}>${esc(c.naam)}${c.plaats ? ' (' + esc(c.plaats) + ')' : ''}</option>`).join('');

// Club bij het lidmaatschap. Zonder club kiest u er een; met een club staat hij vast
// (wisselen gaat via een overstap). Een vrijstelling zet de bond via een voorstel.
// Wijzigingen (opzegging, overstap, tweede club) loopt via een verzoek, ook als de bond ze namens het lid indient.
function wijzigKnoppen(rel) {
  if (!rel?.id || rel.status !== 'actief' || !heeft('case.write')) return '';
  const k = (soort, icoon, tekst) => `<button type="button" class="btn btn-default btn-sm" data-lm="wijzig" data-relation="${esc(rel.id)}"
    data-soort="${soort}" data-verplicht="1" data-als="bond"${stilzwijgenBond ? ' data-stil="1"' : ''}><i class="ti ti-${icoon}"></i> ${esc(tekst)}</button>`;
  return `<div style="display:flex;gap:6px;flex-wrap:wrap;margin:6px 0 10px">
    ${rel.club_register_id && !rel.vrijstelling ? k('overstap', 'arrows-exchange', t('lidm.knop.overstappen')) + k('tweede_club', 'plus', t('lidm.knop.tweede')) : ''}
    ${k('opzegging', 'door-exit', t('lidm.knop.opzeggen'))}</div>`;
}
function clubVeld(rel, schrijven) {
  if (rel?.club_register_id) {
    return `<div class="form-row"><div class="form-group"><label class="form-label">${esc(t('leden.club'))}</label>
      <input class="form-input" value="${esc(clubNaam(rel.club_register_id))}" disabled>
      <div style="font-size:11px;color:var(--text-3);margin-top:3px">${esc(t('leden.club.wissel'))}</div></div></div>${wijzigKnoppen(rel)}`;
  }
  if (rel?.vrijstelling) {
    return `<div class="banner banner-blue"><i class="ti ti-shield-check"></i><span>${esc(t('leden.club.vrijstelling.' + rel.vrijstelling))}</span></div>${wijzigKnoppen(rel)}`;
  }
  if (!clubsRegister.length) return `<div class="banner banner-amber"><i class="ti ti-building-community"></i><span>${esc(t('leden.club.leeg'))}</span></div>`;
  return `<div class="form-row"><div class="form-group"><label class="form-label">${esc(t('leden.club'))}${rel ? '' : ' *'}</label>
    <select class="form-input" name="lid_club" ${schrijven ? '' : 'disabled'}>${clubOpties('', t('leden.club.kies'))}</select>
    <div style="font-size:11px;color:var(--text-3);margin-top:3px">${esc(t('leden.club.nodig'))}</div></div></div>`;
}

// ── Lijst ──────────────────────────────────────────────────────────────────
export async function renderLeden() {
  const el = document.getElementById('ledenInhoud');
  if (!el) return;
  koppelEvents(el);
  el.innerHTML = '<div class="spinner"></div>';

  const [p, r, d, a] = await Promise.all([
    sb().from('persons').select('*').eq('organisation_id', state.currentOrgId).is('anonymised_at', null).order('achternaam'),
    sb().from('person_relations').select('*').eq('organisation_id', state.currentOrgId).eq('kind', 'lidmaatschap'),
    sb().from('person_duplicates').select('person_a').eq('organisation_id', state.currentOrgId),
    sb().from('aflopende_ouderrelaties').select('id').eq('organisation_id', state.currentOrgId),
  ]);
  const aflopend = a.error ? 0 : (a.data || []).length;
  if (p.error) {
    el.innerHTML = `<div class="banner banner-amber"><i class="ti ti-alert-triangle"></i><span>${esc(t('leden.nietbeschikbaar'))}</span></div>`;
    return;
  }
  personen = p.data || [];
  lidmaatschappen = {};
  for (const rel of r.data || []) {
    const huidig = lidmaatschappen[rel.person_id];
    if (!huidig || (rel.starts_on || '') > (huidig.starts_on || '')) lidmaatschappen[rel.person_id] = rel;
  }
  aantalDubbelen = d.data?.length || 0;
  bondsstatus = await bond.laadStatus(state.currentOrgId);
  await laadClubs();
  await laadBondsleden();

  const schrijven = heeft('member.write');
  el.innerHTML = `
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:16px;flex-wrap:wrap">
      ${schrijven ? knop('nieuw', 'user-plus', t('leden.toevoegen'), 'btn-primary') : ''}
      ${schrijven ? knop('import', 'file-import', t('leden.importeren')) : ''}
      ${heeft('person.export') ? knop('export', 'file-export', t('leden.exporteren')) : ''}
      ${bond.actief() && heeft('org.manage') ? knop('register', 'settings', t('bond.instellingen')) : ''}
      <span style="margin-left:auto"></span>
      ${bondsleden.length ? knop('bondsleden', 'building-community', t('clubleden.knop', { aantal: bondsleden.length }), 'btn-default') : ''}
      ${aantalDubbelen ? knop('dubbelen', 'copy', t('leden.dubbelen', { aantal: aantalDubbelen }), 'btn-ghost') : ''}
    </div>
    ${aflopend ? `<div class="banner banner-amber"><i class="ti ti-cake"></i><span>${esc(t('leden.aflopend', { aantal: aflopend }))}</span></div>` : ''}
    ${zonderClub ? `<div class="banner banner-amber"><i class="ti ti-building-community"></i><span>${esc(t('lidm.werklijst.banner', { aantal: zonderClub }))}</span>
      ${heeft('club.manage') ? knop('werklijst', 'list-check', t('lidm.werklijst.knop', { aantal: zonderClub }), 'btn-default') : ''}</div>` : ''}
    <div id="ledenTabel"></div>`;
  tekenTabel();
}

function tekenTabel() {
  const status = p => {
    const rel = lidmaatschappen[p.id];
    if (!rel) return '';
    return t('leden.status.' + rel.status);
  };
  tabelTeken('leden', 'ledenTabel', [
    { veld: 'achternaam', label: t('leden.kol.naam'),
      tekst: p => `${p.achternaam || ''} ${p.voornaam || ''} ${p.tussenvoegsel || ''}`,
      cel: p => `<strong>${esc([p.achternaam, [p.voornaam, p.tussenvoegsel].filter(Boolean).join(' ')].filter(Boolean).join(', '))}</strong>${p.identity_id
        ? ` <span class="badge badge-teal" title="${esc(t('leden.zelfbeheer.uitleg'))}"><i class="ti ti-user-check" style="font-size:10px"></i> ${esc(t('leden.zelfbeheer'))}</span>` : ''}${bond.schorsingBadge(bondsstatus[p.id])}` },
    { veld: 'geboortedatum', label: t('leden.kol.geboortedatum'), filter: false, tekst: p => p.geboortedatum || '', cel: p => esc(datum(p.geboortedatum)) },
    { veld: 'leeftijd', label: t('leden.kol.leeftijd'), uitlijn: 'right', mono: true, filter: false, tekst: p => leeftijd(p.geboortedatum) },
    { veld: 'lidnummer', label: t('leden.kol.lidnummer'), mono: true },
    { veld: 'ref', label: t('ref.kol'), mono: true, filter: false },
    { veld: 'trainingsgroep', label: t('leden.kol.groep') },
    ...(bond.actief() ? [{ veld: 'graad', label: t('bond.kol.graad'), tekst: p => bondsstatus[p.id]?.graad || '' },
      { veld: 'club', label: t('leden.kol.club'), tekst: p => {
          const rel = lidmaatschappen[p.id];
          return clubNaam(rel?.club_register_id) || (rel?.vrijstelling ? t('leden.club.vrijstelling.' + rel.vrijstelling) : '');
        } }] : []),
    { veld: 'email', label: t('leden.kol.email') },
    { veld: 'plaats', label: t('leden.kol.plaats') },
    { veld: 'status', label: t('leden.kol.status'), tekst: status },
    // C3: een verzoek over dit lid openen, zonder eerst het formulier te openen.
    ...(bond.actief() && heeft('case.write') ? [{ veld: 'verzoek', label: '', filter: false, tekst: () => '',
      cel: p => `<button type="button" class="btn btn-ghost btn-sm" data-actie="verzoek-rij" data-id="${esc(p.id)}"
        title="${esc(t('leden.verzoek.knop'))}" aria-label="${esc(t('leden.verzoek.knop'))}"><i class="ti ti-message-plus"></i></button>` }] : []),
  ], personen, {
    zoekLabel: t('leden.zoek'),
    leeg: t('leden.leeg'),
    sorteerVeld: 'achternaam',
    rijKlik: p => openLid(p),
  });
}

// ── Events ─────────────────────────────────────────────────────────────────
function koppelEvents(el) {
  if (el.dataset.gekoppeld) return;
  el.dataset.gekoppeld = '1';
  // Een knop in een rij: in de capture-fase afvangen, zodat de rij zelf (die het
  // ledenformulier opent) de klik niet krijgt.
  el.addEventListener('click', e => {
    const k = e.target.closest('[data-actie="verzoek-rij"]');
    if (!k) return;
    e.stopPropagation();
    openVerzoekOver(personen.find(p => p.id === k.dataset.id));
  }, true);
  el.addEventListener('click', e => {
    const actie = e.target.closest('[data-actie]')?.dataset.actie;
    if (actie === 'nieuw') openLid(null);
    if (actie === 'import') openImport();
    if (actie === 'export') openExport();
    if (actie === 'dubbelen') openDubbelen();
    if (actie === 'register') bond.openInstellingen();
    if (actie === 'werklijst') openWerklijst();
    if (actie === 'bondsleden') openBondsleden();
  });
  document.addEventListener('taalgewijzigd', () => {
    if (document.getElementById('pageLeden')?.classList.contains('active')) renderLeden();
  });
}

function modal() {
  let m = document.getElementById('ledenModal');
  if (!m) {
    m = document.createElement('div');
    m.className = 'modal-overlay';
    m.id = 'ledenModal';
    m.innerHTML = '<div class="modal" style="max-width:760px"><div id="ledenModalInhoud" style="display:flex;flex-direction:column;min-height:0;flex:1"></div></div>';
    document.body.appendChild(m);
    m.addEventListener('click', e => {
      if (e.target === m || e.target.closest('[data-actie="sluit"]')) sluitModal();
    });
    // Knoppen binnen een formulier zijn standaard submitknoppen; een submit
    // herlaadt de pagina. Alle handelingen lopen hier via data-actie.
    m.addEventListener('submit', e => e.preventDefault());
  }
  return m;
}
function toonModal(titel, lichaam, voet) {
  const m = modal();
  m.querySelector('#ledenModalInhoud').innerHTML = `
    <div class="modal-header"><span class="modal-title">${esc(titel)}</span>
      <button class="btn-icon" data-actie="sluit" title="${esc(t('alg.sluiten'))}"><i class="ti ti-x"></i></button></div>
    <div class="modal-body">${lichaam}</div>
    ${voet ? `<div class="modal-footer">${voet}</div>` : ''}`;
  m.classList.add('open');
  return m.querySelector('#ledenModalInhoud');
}
// ── Werklijst: leden zonder club koppelen ──────────────────────────────────
async function openWerklijst(melding) {
  const { data, error } = await sb().from('lidmaatschap_werklijst')
    .select('relation_id, person_id, naam, club_naam, club_sleutel, systeem').eq('organisation_id', state.currentOrgId);
  if (error) { fout(error); return; }
  const groepen = new Map();
  for (const r of data || []) {
    const sleutel = r.club_sleutel || '';
    const naam = (r.club_naam || '').trim();
    const k = sleutel ? `s|${r.systeem}|${sleutel}` : `n|${naam.toLowerCase()}`;
    if (!groepen.has(k)) groepen.set(k, { sleutel, naam, systeem: r.systeem, leden: [] });
    groepen.get(k).leden.push(r);
  }
  const lijst = [...groepen.values()].sort((a, b) => (a.naam || a.sleutel ? 0 : 1) - (b.naam || b.sleutel ? 0 : 1) || a.naam.localeCompare(b.naam));
  const lichaam = `
    ${melding ? `<div class="banner banner-green"><i class="ti ti-check"></i><span>${esc(melding)}</span></div>` : ''}
    <p style="font-size:12px;color:var(--text-3)">${esc(t('lidm.werklijst.uitleg'))}</p>
    ${lijst.length ? `<div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;flex-wrap:wrap">
      ${knop('wl-auto', 'wand', t('lidm.werklijst.auto'))}
      <span style="font-size:11px;color:var(--text-3)">${esc(t('lidm.werklijst.autoUitleg'))}</span></div>` : ''}
    ${lijst.map((g, i) => `<div class="org-row" data-groep="${i}" style="align-items:flex-end;flex-wrap:wrap">
        <div style="flex:1;min-width:200px">
          <div style="font-size:13px;font-weight:600">${esc(t('lidm.werklijst.groep', { naam: g.naam || t('lidm.werklijst.zonderSleutel'), aantal: g.leden.length }))}</div>
          <div style="font-size:11px;color:var(--text-3)">${g.sleutel ? esc(t('lidm.werklijst.bron', { systeem: g.systeem, sleutel: g.sleutel })) + ' &middot; ' : ''}${esc(g.leden.slice(0, 4).map(l => l.naam).join(', '))}${g.leden.length > 4 ? ' ...' : ''}</div>
        </div>
        ${g.naam || g.sleutel ? `<select class="form-input" style="max-width:240px" name="wl_club_${i}" aria-label="${esc(t('lidm.werklijst.kies'))}">${clubOpties('', t('lidm.werklijst.kies'))}</select>
        ${knop('wl-koppel', 'link', t('lidm.werklijst.koppel'), 'btn-primary', `data-groep="${i}"`)}`
          : `<span style="font-size:11px;color:var(--text-3)">${esc(t('lidm.fout.sleutel_nodig'))}</span>`}
      </div>`).join('') || `<p style="font-size:13px">${esc(t('lidm.werklijst.geen'))}</p>`}`;
  const inhoud = toonModal(t('lidm.werklijst.titel'), lichaam,
    `<button class="btn btn-ghost" data-actie="sluit">${esc(t('alg.sluiten'))}</button>`);
  const klaar = async (aantal) => {
    await renderLeden();
    openWerklijst(aantal ? t('lidm.werklijst.gekoppeld', { aantal }) : t('lidm.werklijst.niets'));
  };
  inhoud.onclick = async e => {
    const a = e.target.closest('[data-actie]')?.dataset.actie;
    if (a === 'wl-auto') {
      const { data: n, error: fe } = await sb().rpc('club_leden_koppelen_automatisch', { p_org: state.currentOrgId });
      if (fe) { fout(fe); return; }
      await klaar(n);
    }
    if (a === 'wl-koppel') {
      const i = Number(e.target.closest('[data-groep]').dataset.groep);
      const g = lijst[i];
      const club = inhoud.querySelector(`[name="wl_club_${i}"]`)?.value;
      if (!club) { toast(t('lidm.werklijst.kies'), 'error'); return; }
      const { data: n, error: fe } = await sb().rpc('club_leden_koppelen', {
        p_org: state.currentOrgId, p_club: club, p_club_sleutel: g.sleutel || null, p_club_naam: g.sleutel ? null : g.naam, p_systeem: g.systeem });
      if (fe) { fout(fe); return; }
      await klaar(n);
    }
  };
}

function sluitModal() { document.getElementById('ledenModal')?.classList.remove('open'); }

// ── Bond: verzoek over een lid (C3) ────────────────────────────────────────
async function openVerzoekOver(p) {
  if (!p) return;
  const rel = lidmaatschappen[p.id];
  await nieuwVerzoekVenster(sb(), {
    titel: t('leden.verzoek.titel', { naam: volledigeNaam(p) }),
    uitleg: t('leden.verzoek.uitleg'),
    args: { p_bond: state.currentOrgId, p_person: p.id, p_club_register: rel?.club_register_id || null },
  });
}

// ── Club: leden bij de bond (stap 4) ───────────────────────────────────────
// Eén persoonsrecord bij de bond; de club leest via club_bondsleden() wie nu bij
// haar staat en start van daaruit een opzegging of overstap. Na een overstap staat
// het lid hier niet meer (tests/rls/33_club_bondsleden.sql).
async function laadBondsleden() {
  bondsleden = [];
  if (bond.actief()) return;
  const { data, error } = await sb().rpc('club_bondsleden', { p_club_org: state.currentOrgId });
  if (!error) bondsleden = data || [];
}

function openBondsleden() {
  const knopWijzig = (r, soort, icoon, tekst) => `<button type="button" class="btn btn-default btn-sm" data-lm="wijzig" data-relation="${esc(r.relation_id)}"
    data-soort="${soort}" data-verplicht="1" data-als="club"${r.stilzwijgen ? ' data-stil="1"' : ''}><i class="ti ti-${icoon}"></i> ${esc(tekst)}</button>`;
  const rijen = bondsleden.map(r => `<div class="org-row" style="align-items:center;flex-wrap:wrap" data-bondslid="${esc(r.relation_id)}">
      <div style="flex:1;min-width:220px">
        <div style="font-size:13px;font-weight:600">${esc(r.persoon_naam)}${r.lidnummer ? ` <span style="font-family:var(--mono);font-size:11px;color:var(--text-3)">${esc(r.lidnummer)}</span>` : ''}</div>
        <div style="font-size:11px;color:var(--text-3)">${esc([r.bond_naam, r.club_naam, r.email, r.telefoon].filter(Boolean).join(' · '))}</div>
      </div>
      ${r.lopende_actie ? `<span style="font-size:12px;color:var(--text-3)">${esc(t('lidm.lopend'))}</span>`
        : r.kan_wijzigen ? `<div style="display:flex;gap:6px;flex-wrap:wrap">${knopWijzig(r, 'overstap', 'arrows-exchange', t('lidm.knop.overstappen'))}${knopWijzig(r, 'opzegging', 'door-exit', t('lidm.knop.opzeggen'))}</div>` : ''}
    </div>`).join('');
  const inhoud = toonModal(t('clubleden.titel'), `
    <p style="font-size:12px;color:var(--text-3)">${esc(t('clubleden.uitleg'))}</p>
    ${rijen || `<p style="font-size:13px">${esc(t('clubleden.leeg'))}</p>`}`,
    `<button class="btn btn-ghost" data-actie="sluit">${esc(t('alg.sluiten'))}</button>`);
  inhoud.onclick = e => {
    const lm = e.target.closest('[data-lm]');
    if (lm) behandelLm(lm, sb(), async () => { await renderLeden(); openBondsleden(); });
  };
}

// ── Detail ─────────────────────────────────────────────────────────────────
const PERSOONSVELDEN = [
  ['voornaam', 'text'], ['tussenvoegsel', 'text'], ['achternaam', 'text'],
  ['geboortedatum', 'date'], ['geslacht', 'geslacht'], ['nationaliteiten', 'lijst'],
];
const CONTACTVELDEN = [
  ['email', 'email'], ['telefoon', 'text'], ['straat', 'text'], ['huisnummer', 'text'],
  ['postcode', 'text'], ['plaats', 'text'], ['land', 'text'],
];
const CLUBVELDEN = [['lidnummer', 'text'], ['trainingsgroep', 'text'], ['graad_opgave', 'text'], ['notities', 'textarea']];
// Velden die een wachtwoordmanager voor een inlogveld aanziet (D3). autocomplete=off
// alleen negeren 1Password en LastPass, daarom ook hun eigen attributen.
const GEEN_AUTOVUL = new Set(['lidnummer']);
const geenAutovul = veld => (GEEN_AUTOVUL.has(veld) ? ' autocomplete="off" data-1p-ignore data-lpignore="true" data-form-type="other"' : '');

function veldUitleg(veld, p, eig) {
  const soort = eig[veld];
  if (!heeft('member.write')) return { dicht: true, uitleg: '' };
  if (soort === 'fixed' && p?.[veld]) return { dicht: true, uitleg: t('leden.veld.vast') };
  if (soort === 'person' && p?.identity_id) return { dicht: true, uitleg: t('leden.veld.persoon') };
  return { dicht: false, uitleg: '' };
}

function invoer(veld, type, p, eig) {
  const { dicht, uitleg } = veldUitleg(veld, p, eig);
  const w = p?.[veld];
  const attr = `name="${veld}" ${dicht ? 'disabled' : ''}`;
  let html;
  if (type === 'geslacht') {
    html = `<select class="form-input" ${attr}><option value=""></option>${['m', 'v', 'x']
      .map(g => `<option value="${g}"${w === g ? ' selected' : ''}>${esc(t('leden.geslacht.' + g))}</option>`).join('')}</select>`;
  } else if (type === 'textarea') {
    html = `<textarea class="form-input" rows="2" ${attr}>${esc(w || '')}</textarea>`;
  } else if (type === 'lijst') {
    html = `<input class="form-input" type="text" ${attr} value="${esc((w || []).join(', '))}" placeholder="NL, BE">`;
  } else {
    html = `<input class="form-input" type="${type}" ${attr}${geenAutovul(veld)} value="${esc(w || '')}">`;
  }
  return `<div class="form-group"><label class="form-label">${esc(t('leden.veld.' + veld))}</label>${html}${uitleg
    ? `<div style="font-size:11px;color:var(--text-3);margin-top:3px"><i class="ti ti-lock" style="font-size:11px"></i> ${esc(uitleg)}</div>` : ''}</div>`;
}

function raster(velden, p, eig) {
  return `<div class="form-row">${velden.map(([v, type]) => invoer(v, type, p, eig)).join('')}</div>`;
}

async function openLid(p) {
  const eig = await laadEigenaarschap();
  const id = p?.id || null;
  const [ouders, toestemmingen, paspoort, claims] = id ? await Promise.all([
    sb().from('guardianships').select('*').eq('child_person_id', id).order('created_at'),
    sb().from('current_consents').select('*').eq('person_id', id),
    heeft('passport.manage') ? sb().from('person_passports').select('*').eq('person_id', id).maybeSingle() : Promise.resolve({ data: null }),
    sb().from('identity_claims').select('id, guardianship_id, email, expires_at, used_at, revoked_at').eq('person_id', id),
  ]) : [{ data: [] }, { data: [] }, { data: null }, { data: [] }];
  const nu = new Date().toISOString();
  const openClaim = gid => (claims.data || []).find(c => (c.guardianship_id || null) === (gid || null)
    && !c.used_at && !c.revoked_at && c.expires_at > nu);
  const claimKnop = (gid, label) => {
    const open = openClaim(gid);
    return `<button type="button" class="btn btn-default btn-sm" data-actie="claim" data-id="${gid || ''}"><i class="ti ti-send"></i> ${esc(label)}</button>${open
      ? `<span style="font-size:11px;color:var(--text-3);margin-left:6px">${esc(t('leden.claim.open', { datum: datum(open.expires_at) }))}</span>` : ''}`;
  };
  const rel = id ? lidmaatschappen[id] : null;
  const schrijven = heeft('member.write');
  const stand = Object.fromEntries((toestemmingen.data || []).map(c => [c.purpose, c.granted]));

  const ouderRijen = (ouders.data || []).map(g => {
    const actief = !g.ends_on || g.ends_on >= new Date().toISOString().slice(0, 10);
    return `<div class="org-row">
      <div style="flex:1;min-width:200px">
        <div style="font-size:13px">${esc(g.guardian_name || g.guardian_email)} <span style="color:var(--text-3)">${esc(t('leden.ouder.soort.' + g.kind))}</span></div>
        <div style="font-size:11px;color:var(--text-3)">${esc(g.guardian_email || '')}${g.ends_on ? ' &middot; ' + esc(t(actief ? 'leden.ouder.tot' : 'leden.ouder.beeindigd', { datum: datum(g.ends_on) })) : ''}</div>
      </div>
      ${g.guardian_identity_id ? `<span class="badge badge-teal"><i class="ti ti-user-check" style="font-size:10px"></i> ${esc(t('leden.zelfbeheer'))}</span>`
        : (schrijven && actief && g.guardian_email ? claimKnop(g.id, t('leden.claim.ouder')) : '')}
      ${schrijven && actief ? `<button type="button" class="btn btn-ghost btn-sm" data-actie="ouder-stop" data-id="${g.id}"><i class="ti ti-user-minus"></i> ${esc(t('leden.ouder.stop'))}</button>` : ''}
    </div>`;
  }).join('');

  const lichaam = `
    <form id="lidFormulier" autocomplete="off">
      <div class="section-title"><i class="ti ti-user"></i> ${esc(t('leden.sectie.persoon'))}</div>
      ${raster(PERSOONSVELDEN, p, eig)}
      <div class="section-title" style="margin-top:8px"><i class="ti ti-address-book"></i> ${esc(t('leden.sectie.contact'))}</div>
      ${raster(CONTACTVELDEN, p, eig)}
      <div class="section-title" style="margin-top:8px"><i class="ti ti-building"></i> ${esc(t('leden.sectie.club'))}</div>
      ${raster(CLUBVELDEN, p, eig)}
      <div class="section-title" style="margin-top:8px"><i class="ti ti-id-badge-2"></i> ${esc(t('leden.sectie.lidmaatschap'))}</div>
      <div class="form-row">
        <div class="form-group"><label class="form-label">${esc(t('leden.lid.sinds'))}</label>
          <input class="form-input" type="date" name="lid_sinds" value="${esc(rel?.starts_on || '')}" ${schrijven ? '' : 'disabled'}></div>
        <div class="form-group"><label class="form-label">${esc(t('leden.lid.status'))}</label>
          <select class="form-input" name="lid_status" ${schrijven ? '' : 'disabled'}>
            ${['actief', 'beeindigd'].map(s => `<option value="${s}"${(rel?.status || 'actief') === s ? ' selected' : ''}>${esc(t('leden.status.' + s))}</option>`).join('')}
          </select></div>
        <div class="form-group"><label class="form-label">${esc(t('leden.lid.tot'))}</label>
          <input class="form-input" type="date" name="lid_tot" value="${esc(rel?.ends_on || '')}" ${schrijven ? '' : 'disabled'}></div>
      </div>
      ${bond.actief() ? clubVeld(rel, schrijven) : ''}
      ${id ? `
      <div class="section-title" style="margin-top:8px"><i class="ti ti-user-check"></i> ${esc(t('leden.sectie.zelfbeheer'))}</div>
      <div style="font-size:12px;color:var(--text-3);margin-bottom:8px">${esc(t('leden.claim.uitleg'))}</div>
      <div style="margin-bottom:6px">${p.identity_id
        ? `<span class="badge badge-teal"><i class="ti ti-user-check" style="font-size:10px"></i> ${esc(t('leden.zelfbeheer.uitleg'))}</span>`
        : !schrijven ? ''
        : !p.geboortedatum ? `<span style="font-size:12px;color:var(--text-3)">${esc(t('claim.fout.geen_geboortedatum'))}</span>`
        : Number(leeftijd(p.geboortedatum)) < 16 ? `<span style="font-size:12px;color:var(--text-3)">${esc(t('leden.claim.teJong'))}</span>`
        : !p.email ? `<span style="font-size:12px;color:var(--text-3)">${esc(t('claim.fout.geen_email'))}</span>`
        : claimKnop(null, t('leden.claim.zelf'))}</div>
      <div id="claimResultaat"></div>
      <div class="section-title" style="margin-top:8px"><i class="ti ti-users"></i> ${esc(t('leden.sectie.ouders'))}</div>
      <div style="font-size:12px;color:var(--text-3);margin-bottom:8px">${esc(t('leden.ouder.uitleg'))}</div>
      ${ouderRijen || `<p style="font-size:12px;color:var(--text-3)">${esc(t('leden.ouder.geen'))}</p>`}
      ${schrijven ? `<div class="form-row" style="margin-top:8px">
        <div class="form-group"><label class="form-label">${esc(t('leden.ouder.naam'))}</label><input class="form-input" name="ouder_naam"></div>
        <div class="form-group"><label class="form-label">${esc(t('leden.ouder.email'))}</label><input class="form-input" type="email" name="ouder_email"></div>
        <div class="form-group"><label class="form-label">${esc(t('leden.ouder.soort'))}</label>
          <select class="form-input" name="ouder_kind">${['ouder', 'voogd', 'verzorger'].map(k => `<option value="${k}">${esc(t('leden.ouder.soort.' + k))}</option>`).join('')}</select></div>
      </div>` : ''}
      <div class="section-title" style="margin-top:8px"><i class="ti ti-shield-check"></i> ${esc(t('leden.sectie.toestemming'))}</div>
      <div style="font-size:12px;color:var(--text-3);margin-bottom:8px">${esc(t('leden.toestemming.uitleg'))}</div>
      ${DOELEN_TOESTEMMING.map(doel => `<label style="display:flex;align-items:center;gap:8px;font-size:13px;margin-bottom:6px">
          <input type="checkbox" name="toestemming_${doel}" ${stand[doel] ? 'checked' : ''} ${schrijven ? '' : 'disabled'}>
          ${esc(t('leden.toestemming.' + doel))}
          <span style="font-size:11px;color:var(--text-3)">${doel in stand ? '' : esc(t('leden.toestemming.nietgevraagd'))}</span></label>`).join('')}
      ${schrijven ? `<div class="form-group" style="max-width:260px;margin-top:6px"><label class="form-label">${esc(t('leden.toestemming.bron'))}</label>
        <select class="form-input" name="toestemming_bron">${['formulier', 'mondeling'].map(b => `<option value="${b}">${esc(t('leden.toestemming.bron.' + b))}</option>`).join('')}</select></div>` : ''}
      ${heeft('passport.manage') ? `
      <div class="section-title" style="margin-top:8px"><i class="ti ti-passport"></i> ${esc(t('leden.sectie.paspoort'))}</div>
      <div style="font-size:12px;color:var(--text-3);margin-bottom:8px">${esc(t('leden.paspoort.uitleg'))}</div>
      <div class="form-row">
        <div class="form-group"><label class="form-label">${esc(t('leden.paspoort.nummer'))}</label><input class="form-input" name="pp_nummer" value="${esc(paspoort.data?.nummer || '')}"></div>
        <div class="form-group"><label class="form-label">${esc(t('leden.paspoort.land'))}</label><input class="form-input" name="pp_land" value="${esc(paspoort.data?.land || '')}" placeholder="NL"></div>
        <div class="form-group"><label class="form-label">${esc(t('leden.paspoort.vervalt'))}</label><input class="form-input" type="date" name="pp_vervalt" value="${esc(paspoort.data?.vervaldatum || '')}"></div>
      </div>` : ''}` : `<p style="font-size:12px;color:var(--text-3)">${esc(t('leden.naopslaan'))}</p>`}
    </form>
    ${id && bond.actief() ? '<div id="bondSectie"></div>' : ''}
    ${id ? '<div id="dossierSectie"></div>' : ''}`;

  const voet = `
    ${id && heeft('member.delete') ? `<button class="btn btn-ghost" data-actie="lid-weg" style="margin-right:auto;color:var(--red)"><i class="ti ti-trash"></i> ${esc(t('alg.verwijderen'))}</button>` : ''}
    <button class="btn btn-ghost" data-actie="sluit">${esc(t(schrijven ? 'alg.annuleren' : 'alg.sluiten'))}</button>
    ${schrijven ? `<button class="btn btn-primary" data-actie="lid-opslaan"><i class="ti ti-device-floppy"></i> ${esc(t('alg.opslaan'))}</button>` : ''}`;

  const inhoud = toonModal(p ? volledigeNaam(p) : t('leden.nieuw'), lichaam, voet);
  if (p?.ref) inhoud.querySelector('.modal-title')?.insertAdjacentHTML('beforeend', ' ' + refLabel(p.ref));
  if (id) bond.renderSectie(inhoud.querySelector('#bondSectie'), p);
  if (id) toonDossier(inhoud.querySelector('#dossierSectie'), sb(), id);
  inhoud.onclick = async e => {
    const lm = e.target.closest('[data-lm]');
    if (lm) return behandelLm(lm, sb(), () => { sluitModal(); });
    const actie = e.target.closest('[data-actie]')?.dataset.actie;
    if (actie === 'lid-opslaan') await slaLidOp(p, stand);
    if (actie === 'ouder-stop') await stopOuder(e.target.closest('[data-actie]').dataset.id, p);
    if (actie === 'lid-weg') verwijderLid(p);
    if (actie === 'claim') await maakClaim(p, e.target.closest('[data-actie]').dataset.id || null);
    if (actie === 'kopieer-claim') {
      navigator.clipboard?.writeText(document.getElementById('claimLink')?.textContent || '');
      toast(t('leden.claim.gekopieerd'), 'success');
    }
  };
}

function formulierWaarden(form) {
  const w = {};
  for (const el of form.elements) {
    if (!el.name || el.disabled) continue;
    w[el.name] = el.type === 'checkbox' ? el.checked : el.value.trim();
  }
  return w;
}

async function slaLidOp(p, stand) {
  const form = document.getElementById('lidFormulier');
  const w = formulierWaarden(form);
  if (!p && !w.achternaam) { toast(t('leden.achternaamverplicht'), 'error'); return; }

  const velden = {};
  for (const [v] of [...PERSOONSVELDEN, ...CONTACTVELDEN, ...CLUBVELDEN]) {
    if (!(v in w)) continue;
    let nieuw = w[v] === '' ? null : w[v];
    if (v === 'nationaliteiten') nieuw = (w[v] || '').split(/[\s,;/]+/).map(s => s.trim().toUpperCase()).filter(Boolean);
    if (v === 'email' && nieuw) nieuw = nieuw.toLowerCase();
    const oud = p?.[v] ?? (v === 'nationaliteiten' ? [] : null);
    if (JSON.stringify(nieuw) !== JSON.stringify(oud)) velden[v] = nieuw;
  }

  // Bij een bond vraagt een nieuw lidmaatschap een club; controleer dat vóór er een persoon wordt aangemaakt.
  if (bond.actief() && !lidmaatschappen[p?.id] && !w.lid_club && ('lid_sinds' in w || 'lid_status' in w) && clubsRegister.length) {
    toast(t('lidm.fout.club_nodig'), 'error');
    return;
  }

  try {
    let id = p?.id;
    if (!p) {
      const { data, error } = await sb().from('persons')
        .insert([{ organisation_id: state.currentOrgId, ...velden }]).select().single();
      if (error) throw error;
      id = data.id;
    } else if (Object.keys(velden).length) {
      const { error } = await sb().from('persons').update(velden).eq('id', id);
      if (error) throw error;
    }

    // Lidmaatschap
    const rel = lidmaatschappen[id];
    if ('lid_sinds' in w || 'lid_status' in w) {
      const lid = { starts_on: w.lid_sinds || null, ends_on: w.lid_tot || null, status: w.lid_status || 'actief' };
      const club = bond.actief() && w.lid_club ? w.lid_club : null;
      if (club) lid.club_register_id = club;
      if (rel) {
        if (rel.starts_on !== lid.starts_on || rel.ends_on !== lid.ends_on || rel.status !== lid.status || (club && !rel.club_register_id)) {
          const { error } = await sb().from('person_relations').update(lid).eq('id', rel.id);
          if (error) throw error;
        }
      } else {
        const { error } = await sb().from('person_relations')
          .insert([{ person_id: id, organisation_id: state.currentOrgId, kind: 'lidmaatschap', ...lid }]);
        if (error) throw error;
      }
    }

    if (p) {
      // Ouder toevoegen
      if (w.ouder_naam || w.ouder_email) {
        const { error } = await sb().from('guardianships').insert([{
          child_person_id: id, organisation_id: state.currentOrgId,
          guardian_name: w.ouder_naam || null, guardian_email: (w.ouder_email || '').toLowerCase() || null, kind: w.ouder_kind || 'ouder',
        }]);
        if (error) throw error;
      }
      // Toestemmingen: alleen een regel als de stand verandert.
      const nieuwe = DOELEN_TOESTEMMING
        .filter(doel => `toestemming_${doel}` in w && (!!stand[doel]) !== w[`toestemming_${doel}`] && (doel in stand || w[`toestemming_${doel}`]))
        .map(doel => ({ person_id: id, organisation_id: state.currentOrgId, purpose: doel,
                        granted: w[`toestemming_${doel}`], source: w.toestemming_bron || 'formulier' }));
      if (nieuwe.length) {
        const { error } = await sb().from('consents').insert(nieuwe);
        if (error) throw error;
      }
      // Paspoort
      if ('pp_nummer' in w && w.pp_nummer) {
        const { error } = await sb().from('person_passports').upsert([{
          person_id: id, organisation_id: state.currentOrgId, nummer: w.pp_nummer,
          land: (w.pp_land || 'NL').toUpperCase(), vervaldatum: w.pp_vervalt || null,
        }]);
        if (error) throw error;
      }
    }
    toast(t('leden.opgeslagen'), 'success');
    await renderLeden();
    const bijgewerkt = personen.find(x => x.id === id);
    if (!p && bijgewerkt) openLid(bijgewerkt); else sluitModal();
  } catch (e) { fout(e); }
}

// Uitnodiging om het record te claimen. De link gaat één keer over de lijn;
// de club stuurt hem zelf door, net als bij uitnodigingen voor beheerders.
async function maakClaim(p, gid) {
  const { data, error } = await sb().rpc('claim_aanmaken', { p_person: p.id, p_guardianship: gid });
  if (error) { toast(claimFout(error), 'error'); return; }
  const rij = Array.isArray(data) ? data[0] : data;
  const link = `${location.origin}${location.pathname}?claim=${encodeURIComponent(rij.token)}`;
  const el = document.getElementById('claimResultaat');
  if (el) el.innerHTML = `
    <div class="banner banner-green" style="margin-top:8px"><i class="ti ti-check"></i>
      <span>${esc(t('leden.claim.klaar', { email: rij.email, datum: datum(rij.expires_at) }))}</span></div>
    <div class="invite-link"><span style="flex:1" id="claimLink">${esc(link)}</span>
      <button class="btn btn-default btn-sm" type="button" data-actie="kopieer-claim"><i class="ti ti-copy"></i> ${esc(t('leden.claim.kopieer'))}</button></div>`;
  el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

async function stopOuder(gid, p) {
  const { error } = await sb().from('guardianships')
    .update({ ends_on: new Date().toISOString().slice(0, 10), ended_reason: 'beëindigd door de club' }).eq('id', gid);
  if (error) { fout(error); return; }
  openLid(p);
}

// Verwijderen of anonimiseren loopt via de dispatcher, met reden en vierogen.
// Verwijderen kan alleen zonder geschiedenis (graad, licentie, schorsing,
// inschrijving); anders anonimiseert de database, zodat sportieve feiten en
// financiële regels blijven kloppen.
async function verwijderLid(p) {
  const r = await vraagReden({
    titel: t('leden.weg.titel'),
    uitleg: t('leden.weg.uitleg2', { naam: volledigeNaam(p) }),
    knop: t('leden.weg.knop'),
    gevaar: true,
    voorbeeld: t('reden.voorbeeld.lid'),
    keuzes: [
      { waarde: 'anonimiseren', label: t('leden.weg.anonimiseren'), uitleg: t('leden.weg.anonimiseren.uitleg') },
      { waarde: 'verwijderen', label: t('leden.weg.verwijderen'), uitleg: t('leden.weg.verwijderen.uitleg') },
    ],
  });
  if (!r) return;
  const { data, error } = await sb().rpc('dispatch', {
    p_handeling: 'person.remove', p_gegevens: { person_id: p.id, wijze: r.keuze }, p_reden: r.reden,
  });
  if (error) { toast(dispatchFout(error), 'error'); return; }
  toast(t(data?.status === 'wacht' ? 'vierogen.ingediend' : 'vierogen.uitgevoerd'), 'success');
  await vernieuwTeller();
  sluitModal();
  await renderLeden();
}

// ── Dubbelen ───────────────────────────────────────────────────────────────
async function openDubbelen() {
  const { data, error } = await sb().from('person_duplicates').select('*').eq('organisation_id', state.currentOrgId);
  if (error) { fout(error); return; }
  const naam = id => { const p = personen.find(x => x.id === id); return p ? volledigeNaam(p) : id; };
  const inhoud = toonModal(t('leden.dubbelen.titel'), `
    <p style="font-size:13px;color:var(--text-2);margin-bottom:12px">${esc(t('leden.dubbelen.uitleg'))}</p>
    ${(data || []).map(d => `<div class="org-row">
      <div style="flex:1;font-size:13px">${esc(naam(d.person_a))} <span style="color:var(--text-3)">&harr;</span> ${esc(naam(d.person_b))}
        <div style="font-size:11px;color:var(--text-3)">${esc(t('leden.dubbelen.reden.' + (d.reden === 'naam en geboortedatum' ? 'naam' : 'email')))}</div></div>
      <button class="btn btn-ghost btn-sm" data-actie="open" data-id="${d.person_a}"><i class="ti ti-eye"></i> 1</button>
      <button class="btn btn-ghost btn-sm" data-actie="open" data-id="${d.person_b}"><i class="ti ti-eye"></i> 2</button>
    </div>`).join('')}`, `<button class="btn btn-default" data-actie="sluit">${esc(t('alg.sluiten'))}</button>`);
  inhoud.onclick = e => {
    const k = e.target.closest('[data-actie="open"]');
    if (k) openLid(personen.find(x => x.id === k.dataset.id));
  };
}

// ── Import ─────────────────────────────────────────────────────────────────
let importStaat = null;

function openImport() {
  importStaat = { stap: 'bestand' };
  const inhoud = toonModal(t('import.titel'), `
    <p style="font-size:13px;color:var(--text-2);margin-bottom:12px">${esc(t('import.uitleg'))}</p>
    <ul style="font-size:12px;color:var(--text-3);margin:0 0 14px 18px;line-height:1.7">
      <li>${esc(t('import.tip.perpersoon'))}</li>
      <li>${esc(t('import.tip.paspoort'))}</li>
      <li>${esc(t('import.tip.dubbel'))}</li>
    </ul>
    <input type="file" id="importBestand" accept=".xlsx,.csv" class="form-input">
    <div id="importFout"></div>`,
    `<button class="btn btn-ghost" data-actie="sluit">${esc(t('alg.annuleren'))}</button>`);
  inhoud.querySelector('#importBestand').addEventListener('change', e => leesBestand(e.target.files[0]));
}

async function leesBestand(bestand) {
  if (!bestand) return;
  try {
    let rijen;
    if (/\.csv$/i.test(bestand.name)) {
      rijen = leesCsv(await bestand.text());
    } else {
      const { default: leesExcel } = await import('read-excel-file/browser');
      const bladen = await leesExcel(bestand);
      rijen = bladen[0]?.data || [];
    }
    if (rijen.length < 2) throw new Error(t('import.leeg'));
    const [koppen, ...data] = rijen;
    const mapping = stelMappingVoor(koppen, data);
    // Een clubkolom koppelen kan alleen een bond, aan haar clubregister.
    if (!bond.actief()) mapping.forEach(m => { if (m.doel === 'club') m.doel = 'negeren'; });
    importStaat = { stap: 'koppelen', naam: bestand.name, koppen, rijen: data, mapping };
    toonKoppelen();
  } catch (e) {
    document.getElementById('importFout').innerHTML =
      `<div class="banner banner-red" style="margin-top:12px"><i class="ti ti-alert-circle"></i><span>${esc(t('import.onleesbaar', { melding: e.message }))}</span></div>`;
  }
}

function toonKoppelen() {
  const s = importStaat;
  const opties = huidig => DOELEN.filter(d => d !== 'club' || bond.actief())
    .map(d => `<option value="${d}"${d === huidig ? ' selected' : ''}>${esc(t('import.doel.' + d))}</option>`).join('');
  const inhoud = toonModal(t('import.koppelen.titel', { bestand: s.naam }), `
    <p style="font-size:13px;color:var(--text-2);margin-bottom:12px">${esc(t('import.koppelen.uitleg', { aantal: s.rijen.length }))}</p>
    <div class="table-wrap"><table class="tabel-standaard">
      <thead><tr><th>${esc(t('import.kol.kolom'))}</th><th>${esc(t('import.kol.voorbeeld'))}</th><th style="width:220px">${esc(t('import.kol.veld'))}</th></tr></thead>
      <tbody>${s.mapping.map(m => `<tr>
        <td><strong>${esc(m.kop)}</strong></td>
        <td style="font-size:12px;color:var(--text-3)">${esc(m.voorbeeld.join(' · '))}</td>
        <td>${GEWEIGERD.includes(m.doel)
          ? `<span class="badge badge-red" title="${esc(t('import.geweigerd.' + m.doel))}"><i class="ti ti-ban" style="font-size:10px"></i> ${esc(t('import.nietimporteren'))}</span>
             <div style="font-size:11px;color:var(--text-3);margin-top:3px">${esc(t('import.geweigerd.' + m.doel))}</div>`
          : `<select class="form-input" style="padding:5px 8px;font-size:12px" data-kolom="${m.index}">${opties(m.doel)}</select>`}</td>
      </tr>`).join('')}</tbody></table></div>`,
    `<button class="btn btn-ghost" data-actie="sluit">${esc(t('alg.annuleren'))}</button>
     <button class="btn btn-primary" data-actie="import-controleer"><i class="ti ti-arrow-right"></i> ${esc(t('import.controleren'))}</button>`);
  inhoud.onclick = e => {
    if (e.target.closest('[data-actie="import-controleer"]')) {
      inhoud.querySelectorAll('select[data-kolom]').forEach(sel => {
        const m = s.mapping.find(x => x.index === Number(sel.dataset.kolom));
        if (m) m.doel = sel.value;
      });
      toonControle();
    }
  };
}

function melding(m) { return t('import.melding.' + m.code, m.waarden); }

// Clubkolom van een bond: per schrijfwijze een koppeling met het register (stap 2).
// Exact koppelt vanzelf, een voorstel bevestigt de beheerder, de rest gaat naar
// de werklijst "Leden zonder club". De import stopt er nooit op.
const CLUB_BADGE = { exact: 'badge-green', voorstel: 'badge-amber', keuze: 'badge-amber', onbekend: 'badge-grey' };
async function laadAliassen() {
  const { data } = await sb().from('external_ids').select('entity_id, value')
    .eq('organisation_id', state.currentOrgId).eq('entity', 'club').eq('system', 'naam');
  return data || [];
}
function clubKoppelingHtml(koppeling) {
  const opties = (regel) => {
    const voor = regel.kandidaten.filter(id => id !== regel.club_id);
    const naam = id => clubsRegister.find(c => c.id === id);
    const optie = c => `<option value="${c.id}"${c.id === regel.club_id ? ' selected' : ''}>${esc(c.naam)}${c.plaats ? ' (' + esc(c.plaats) + ')' : ''}</option>`;
    return `<option value="">${esc(t('import.clubs.werklijst'))}</option>`
      + (regel.status === 'keuze' && voor.length ? `<optgroup label="${esc(t('import.clubs.lijktOp'))}">${voor.map(naam).filter(Boolean).map(optie).join('')}</optgroup>` : '')
      + clubsRegister.map(optie).join('');
  };
  return `<div class="section-title" style="margin-top:4px"><i class="ti ti-building-community"></i> ${esc(t('import.clubs.titel'))}</div>
    <p style="font-size:12px;color:var(--text-3);margin-bottom:8px">${esc(t('import.clubs.uitleg'))}</p>
    <div class="table-wrap" style="margin-bottom:14px"><table class="tabel-standaard">
      <thead><tr><th>${esc(t('import.clubs.kol.bestand'))}</th><th style="width:60px">${esc(t('import.clubs.kol.leden'))}</th>
        <th style="width:260px">${esc(t('import.clubs.kol.club'))}</th><th>${esc(t('import.clubs.kol.stand'))}</th></tr></thead>
      <tbody>${koppeling.map((r, i) => `<tr data-clubregel="${i}">
        <td>${r.waarde ? `<strong>${esc(r.waarde)}</strong>` : `<span style="color:var(--text-3)">${esc(t('import.clubs.leeg'))}</span>`}</td>
        <td style="font-family:var(--mono)">${r.aantal}</td>
        <td><select class="form-input" style="padding:5px 8px;font-size:12px" name="imp_club_${i}" aria-label="${esc(t('import.clubs.kol.club'))}">${opties(r)}</select></td>
        <td><span class="badge ${CLUB_BADGE[r.status]}">${esc(t('import.clubs.status.' + r.status))}</span>
          ${r.status === 'voorstel' ? `<label style="display:flex;align-items:center;gap:6px;font-size:12px;margin-top:4px">
            <input type="checkbox" name="imp_bev_${i}"> ${esc(t('import.clubs.bevestig'))}</label>` : ''}</td>
      </tr>`).join('')}</tbody></table></div>`;
}
// De gekozen club per schrijfwijze. Een voorstel telt pas na bevestigen.
function leesClubKoppeling(inhoud, koppeling) {
  const keuze = new Map(); const nieuweAliassen = [];
  koppeling.forEach((r, i) => {
    let club = inhoud.querySelector(`[name="imp_club_${i}"]`)?.value || '';
    if (r.status === 'voorstel' && club === r.club_id && !inhoud.querySelector(`[name="imp_bev_${i}"]`)?.checked) club = '';
    keuze.set(r.waarde, club);
    // Een schrijfwijze die niet al exact klopte, onthouden voor de volgende import.
    if (club && r.waarde && !(r.status === 'exact' && club === r.club_id)) nieuweAliassen.push({ club, waarde: r.waarde });
  });
  return { keuze, nieuweAliassen };
}

async function toonControle() {
  const s = importStaat;
  s.uitkomst = verwerk(s.rijen, s.mapping, { bestaand: personen });
  const { samenvatting, personen: rijen } = s.uitkomst;
  s.metClubkolom = bond.actief() && s.mapping.some(m => m.doel === 'club');
  if (s.metClubkolom) {
    if (!s.aliassen) s.aliassen = await laadAliassen();
    s.koppeling = koppelClubs(rijen.filter(r => r.status === 'nieuw').map(r => r.club || ''), clubsRegister, s.aliassen);
  }
  const opmerkingen = rijen.filter(r => r.fouten.length || r.waarschuwingen.length).slice(0, 100);
  const inhoud = toonModal(t('import.controle.titel'), `
    <div style="display:flex;gap:10px;margin-bottom:14px;flex-wrap:wrap">
      <span class="badge badge-green">${esc(t('import.controle.nieuw', { aantal: samenvatting.nieuw }))}</span>
      <span class="badge badge-grey">${esc(t('import.controle.overslaan', { aantal: samenvatting.overslaan }))}</span>
      <span class="badge badge-red">${esc(t('import.controle.fout', { aantal: samenvatting.fout }))}</span>
    </div>
    ${samenvatting.geweigerd.length ? `<div class="banner banner-amber"><i class="ti ti-alert-triangle"></i><span>${esc(t('import.controle.geweigerd', { kolommen: samenvatting.geweigerd.map(g => g.kop).join(', ') }))}</span></div>` : ''}
    ${s.metClubkolom ? clubKoppelingHtml(s.koppeling) : bond.actief() ? `<div class="form-group" style="max-width:360px"><label class="form-label">${esc(t('import.club'))} *</label>
      <select class="form-input" name="import_club">${clubOpties(s.club || '', t('leden.club.kies'))}</select>
      <div style="font-size:11px;color:var(--text-3);margin-top:3px">${esc(t('import.club.uitleg'))}</div></div>` : ''}
    ${opmerkingen.length ? `<div class="table-wrap"><table class="tabel-standaard">
      <thead><tr><th style="width:60px">${esc(t('import.kol.rij'))}</th><th>${esc(t('import.kol.naam'))}</th><th>${esc(t('import.kol.opmerking'))}</th></tr></thead>
      <tbody>${opmerkingen.map(r => `<tr>
        <td style="font-family:var(--mono)">${r.rij}</td>
        <td>${esc(volledigeNaam(r.persoon) || '—')}</td>
        <td style="font-size:12px">${[...r.fouten.map(f => `<span style="color:var(--red)">${esc(melding(f))}</span>`),
                                     ...r.waarschuwingen.map(w => `<span style="color:var(--text-2)">${esc(melding(w))}</span>`)].join('<br>')}</td>
      </tr>`).join('')}</tbody></table></div>` : `<p style="font-size:13px">${esc(t('import.controle.schoon'))}</p>`}`,
    `<button class="btn btn-ghost" data-actie="import-terug"><i class="ti ti-arrow-left"></i> ${esc(t('alg.terug'))}</button>
     <button class="btn btn-primary" data-actie="import-start" ${samenvatting.nieuw ? '' : 'disabled'}><i class="ti ti-upload"></i> ${esc(t('import.start', { aantal: samenvatting.nieuw }))}</button>`);
  inhoud.onclick = e => {
    const actie = e.target.closest('[data-actie]')?.dataset.actie;
    if (actie === 'import-terug') toonKoppelen();
    if (actie === 'import-start') {
      if (s.metClubkolom) {
        Object.assign(s, leesClubKoppeling(inhoud, s.koppeling));
      } else {
        s.club = inhoud.querySelector('[name="import_club"]')?.value || '';
        if (bond.actief() && !s.club) { toast(t('import.club.nodig'), 'error'); return; }
      }
      voerImportUit();
    }
  };
}

async function voerImportUit() {
  const s = importStaat;
  const nieuw = s.uitkomst.personen.filter(r => r.status === 'nieuw');
  const voortgang = toonModal(t('import.bezig'), `<p id="importVoortgang" style="font-size:13px"></p><div class="spinner"></div>`, '');
  const zet = n => { const el = voortgang.querySelector('#importVoortgang'); if (el) el.textContent = t('import.voortgang', { klaar: n, totaal: nieuw.length }); };
  let gelukt = 0; let opWerklijst = 0; const mislukt = [];
  for (let i = 0; i < nieuw.length; i += 50) {
    const blok = nieuw.slice(i, i + 50);
    const { data, error } = await sb().from('persons')
      .insert(blok.map(r => ({ organisation_id: state.currentOrgId, ...r.persoon }))).select('id');
    if (error) { blok.forEach(r => mislukt.push({ rij: r.rij, melding: error.message })); zet(i + blok.length); continue; }
    gelukt += data.length;
    const relaties = []; const zonderClub = []; const ouders = [];
    data.forEach((rij, k) => {
      const bron = blok[k];
      const club = s.metClubkolom ? s.keuze.get(bron.club || '') : (bond.actief() ? s.club : null);
      if (bond.actief() && !club) zonderClub.push({ person_id: rij.id, starts_on: bron.lidSinds, club_naam: bron.club || null });
      else relaties.push({ person_id: rij.id, organisation_id: state.currentOrgId, kind: 'lidmaatschap', status: 'actief', starts_on: bron.lidSinds,
        ...(club ? { club_register_id: club } : {}) });
      bron.ouders.forEach(o => ouders.push({ child_person_id: rij.id, organisation_id: state.currentOrgId, guardian_name: o.naam, guardian_email: o.email, kind: o.kind }));
    });
    if (relaties.length) {
      const r1 = await sb().from('person_relations').insert(relaties);
      if (r1.error) mislukt.push({ rij: blok[0].rij, melding: r1.error.message });
    }
    // Nog te koppelen: lidmaatschap zonder club, met de naam uit het bestand, op de werklijst.
    if (zonderClub.length) {
      const r3 = await sb().rpc('lidmaatschap_zonder_club_importeren', { p_org: state.currentOrgId, p_rijen: zonderClub });
      if (r3.error) mislukt.push({ rij: blok[0].rij, melding: foutTekst(r3.error) });
      else opWerklijst += zonderClub.length;
    }
    if (ouders.length) {
      const r2 = await sb().from('guardianships').insert(ouders);
      if (r2.error) mislukt.push({ rij: blok[0].rij, melding: r2.error.message });
    }
    zet(i + blok.length);
  }
  // Bevestigde schrijfwijzen onthouden, zodat ze de volgende keer vanzelf koppelen.
  if (s.metClubkolom && heeft('club.manage')) {
    for (const a of s.nieuweAliassen || []) {
      await sb().from('external_ids').insert([{ organisation_id: state.currentOrgId, entity: 'club', entity_id: a.club,
        system: 'naam', value: a.waarde.trim().toLowerCase() }]);
    }
  }
  toonModal(t('import.klaar.titel'), `
    <p style="font-size:13px">${esc(t('import.klaar.uitleg', { aantal: gelukt }))}</p>
    ${opWerklijst ? `<div class="banner banner-amber"><i class="ti ti-building-community"></i><span>${esc(t('import.klaar.werklijst', { aantal: opWerklijst }))}</span></div>` : ''}
    ${mislukt.length ? `<div class="banner banner-red"><i class="ti ti-alert-circle"></i><span>${esc(t('import.klaar.mislukt', { aantal: mislukt.length }))}</span></div>
      <ul style="font-size:12px;margin-left:18px">${mislukt.slice(0, 20).map(m => `<li>${esc(t('import.kol.rij'))} ${m.rij}: ${esc(m.melding)}</li>`).join('')}</ul>` : ''}
    <p style="font-size:12px;color:var(--text-3)">${esc(t('import.klaar.daarna'))}</p>`,
    `<button class="btn btn-primary" data-actie="sluit">${esc(t('alg.sluiten'))}</button>`);
  await renderLeden();
}

// ── Export ─────────────────────────────────────────────────────────────────
async function openExport() {
  const pp = heeft('passport.manage');
  const klaar = await exportMod.klaarstaand('leden');
  const inhoud = toonModal(t('export.titel'), `
    <p style="font-size:13px;color:var(--text-2);margin-bottom:12px">${esc(t('export.uitleg'))}</p>
    <ul style="font-size:12px;color:var(--text-3);margin:0 0 14px 18px;line-height:1.7">
      <li>${esc(t('export.inhoud.json'))}</li><li>${esc(t('export.inhoud.csv'))}</li><li>${esc(t('export.inhoud.manifest'))}</li>
    </ul>
    ${pp ? `<label style="display:flex;align-items:center;gap:8px;font-size:13px"><input type="checkbox" id="exportPaspoort"> ${esc(t('export.paspoort'))}</label>` : ''}
    <p style="font-size:12px;color:var(--text-3);margin-top:12px"><i class="ti ti-history"></i> ${esc(t('export.gelogd2'))}</p>
    ${exportMod.klaarstaandHtml(klaar)}`,
    `<button class="btn btn-ghost" data-actie="sluit">${esc(t('alg.annuleren'))}</button>
     <button class="btn btn-primary" data-actie="export-start"><i class="ti ti-download"></i> ${esc(t('export.start'))}</button>`);
  inhoud.onclick = async e => {
    const ophalen = e.target.closest('[data-export="ophalen"]');
    if (ophalen) { if (await exportMod.ophalen(ophalen.dataset.id)) sluitModal(); return; }
    if (!e.target.closest('[data-actie="export-start"]')) return;
    const uit = await exportMod.aanvragen('leden', { paspoorten: !!inhoud.querySelector('#exportPaspoort')?.checked });
    if (uit) sluitModal();
  };
}
